import { useCallback, useState } from 'react';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';

import { useEvent } from '../utils/analytics/useEvent';
import { MaxLengths } from '../utils/constants';

type AddressComponentMapping = {
  streetNumber: string[];
  postalCode: string[];
  street: string[];
  province: string[];
  city: string[];
  country: string[];
};

export type AddressObject = {
  street: string;
  street2: string;
  postalCode: string;
  province: string;
  city: string;
  country: string;
  [key: string]: string;
};

const ADDRESS_COMPONENT_MAPPING: AddressComponentMapping = {
  streetNumber: ['street_number'],
  postalCode: ['postal_code'],
  street: ['street_address', 'route'],
  province: [
    'administrative_area_level_1',
    'administrative_area_level_2',
    'administrative_area_level_3',
    'administrative_area_level_4',
    'administrative_area_level_5',
  ],
  city: [
    'locality',
    'sublocality',
    'sublocality_level_1',
    'sublocality_level_2',
    'sublocality_level_3',
    'sublocality_level_4',
  ],
  country: ['country'],
};

export const useAddressSearch = (autocompleteSessionToken?: google.maps.places.AutocompleteSessionToken) => {
  const trackEvent = useEvent();
  const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading } = usePlacesService({
    apiKey: process.env.REACT_APP_GOOGLE || '',
  });
  const [addressTracked, setAddressTracked] = useState(false);

  const onInputChange = useCallback(
    (_field: string, input: string) => {
      getPlacePredictions({
        input,
        sessionToken: autocompleteSessionToken,
      });
      if (!addressTracked) {
        trackEvent('identity – m life - registration address', 'm life registration address', 'mliferegaddress');
        setAddressTracked(true);
      }
    },
    [getPlacePredictions, addressTracked, trackEvent, autocompleteSessionToken],
  );

  const getPlaceDetails = useCallback(
    (placeId: string): Promise<{ place: google.maps.places.PlaceResult; address?: AddressObject }> => {
      return new Promise((resolve, reject) => {
        placesService?.getDetails(
          {
            placeId,
            fields: ['address_component'],
            sessionToken: autocompleteSessionToken,
          },
          (result: google.maps.places.PlaceResult | null, status: google.maps.places.PlacesServiceStatus) => {
            if (status !== google.maps.places.PlacesServiceStatus.OK) {
              reject({ place: result });
              return;
            }

            const address: AddressObject = {
              postalCode: '',
              street: '',
              street2: '',
              province: '',
              city: '',
              country: '',
            };
            result?.address_components?.forEach(component => {
              for (const shouldBe in ADDRESS_COMPONENT_MAPPING) {
                if (ADDRESS_COMPONENT_MAPPING[shouldBe as keyof AddressComponentMapping].includes(component.types[0])) {
                  address[shouldBe] = ['province', 'country'].includes(shouldBe)
                    ? component.short_name
                    : component.long_name;
                }
              }
            });

            if (address.streetNumber && address.street) {
              address.street = `${address.streetNumber} ${address.street}`;
            }

            resolve({
              place: result || {},
              address: {
                ...address,
                street: address.street.substring(0, MaxLengths.Address),
                street2:
                  address.street.length > MaxLengths.Address
                    ? `${address.street.substring(MaxLengths.Address)} ${address.street2}`
                    : address.street2,
              },
            });
          },
        );
      });
    },
    [placesService, autocompleteSessionToken],
  );

  return {
    onInputChange,
    placesService,
    placePredictions,
    isPlacePredictionsLoading,
    getPlaceDetails,
  };
};
